'use client';

import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { Box } from '@terra/ui-styled-system';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
  type Node,
  type Edge,
  type ColorMode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Spinner, useThemeMode } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  ExternalNetworkEntityKindSchema,
  type AssetGraphResponseDto,
  type ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import {
  GRAPH_COLORS,
  isSyntheticGraphNodeId,
  seedNodePositions,
  shouldPreserveNodePositions,
  useAutoLayout,
  type AutoLayoutConfig,
  type HierarchyRankDirection,
} from '../flow-layout';
import {
  GRAPH_NODE_COLOR,
  getGraphEdgeTranslationKey,
  type AssetNodeData,
  type ExternalNetworkGraphLayout,
} from './constants';
import {
  buildFlowElements,
  type GraphWebAppCluster,
} from './buildFlowElements';
import type { ExternalNetworkGraphVantagePoint } from './graph-vantage-point';
import { appendApplicationNodes } from './buildFlowApplicationNodes';
import { ExternalNetworkGraphNodeTooltip } from './ExternalNetworkGraphNodeTooltip';
import {
  externalNetworkEdgeTypes,
  externalNetworkNodeTypes,
} from './external-network-graph-elements';
import { ExternalNetworkGraphNodeActionsProvider } from './ExternalNetworkGraphNodeActionsContext';
import type { GraphNodeExpansion } from './resolveGraphNodeExpansion';

const AUTO_LAYOUT_BY_GRAPH_LAYOUT: Record<
  ExternalNetworkGraphLayout,
  NonNullable<AutoLayoutConfig['layout']>
> = {
  standard: 'forceAtlas2',
  radial: 'radial',
  sequential: 'hierarchy',
};

const RANK_DIRECTION_BY_GRAPH_LAYOUT: Record<
  ExternalNetworkGraphLayout,
  HierarchyRankDirection
> = {
  standard: 'LR',
  radial: 'LR',
  sequential: 'TB',
};

type GraphLayoutSpacing = {
  nodesep: number;
  ranksep: number;
  fitViewPadding: number;
  fitViewMaxZoom?: number;
};

const MAIN_LAYOUT_SPACING_BY_GRAPH_LAYOUT: Record<
  ExternalNetworkGraphLayout,
  GraphLayoutSpacing
> = {
  standard: { nodesep: 320, ranksep: 600, fitViewPadding: 0.1 },
  radial: { nodesep: 220, ranksep: 600, fitViewPadding: 0.1 },
  sequential: {
    nodesep: 80,
    ranksep: 500,
    fitViewPadding: 0.1,
    fitViewMaxZoom: 1.2,
  },
};

export type ExternalNetworkGraphFlowProps = {
  graph: AssetGraphResponseDto;
  layout: ExternalNetworkGraphLayout;
  showMiniMap: boolean;
  onlyRenderVisibleElements: boolean;
  onSelectAsset: (id: string, kind: ExternalNetworkEntityKind) => void;
  onExpandNode: (id: string) => void;
  onViewDetails: (nodeId: string) => void;
  onClearSelection: () => void;
  onOpenApplication?: (application: {
    applicationId: string;
    isDraft?: boolean;
  }) => void;
  webAppClusters?: GraphWebAppCluster[];
  selectedNodeId: string | null;
  selectedNodeExpansion?: GraphNodeExpansion;
  isExpanding: boolean;
  focusedAssetId: string | null;
  translationNamespace?: 'externalNetwork';
  vantagePoint?: ExternalNetworkGraphVantagePoint;
};

export function ExternalNetworkGraphFlow({
  graph,
  layout,
  showMiniMap,
  onlyRenderVisibleElements,
  onSelectAsset,
  onExpandNode,
  onViewDetails,
  onClearSelection,
  onOpenApplication,
  webAppClusters,
  selectedNodeId,
  selectedNodeExpansion,
  isExpanding,
  focusedAssetId,
  translationNamespace = 'externalNetwork',
  vantagePoint,
}: ExternalNetworkGraphFlowProps) {
  const t = useTranslations(translationNamespace);
  const { mode: themeMode } = useThemeMode();
  const colorMode: ColorMode = themeMode === 'dark' ? 'dark' : 'light';
  const { setCenter, flowToScreenPosition } =
    useReactFlow<Node<AssetNodeData>>();
  const nodesInitialized = useNodesInitialized();
  const autoLayout = AUTO_LAYOUT_BY_GRAPH_LAYOUT[layout];

  const { flowNodes, flowEdges } = useMemo(
    () =>
      buildFlowElements(
        graph,
        (edgeType) => {
          const translationKey = getGraphEdgeTranslationKey(edgeType);

          return translationKey ? t(translationKey) : edgeType;
        },
        {
          edgeMode: layout === 'radial' ? 'primary' : 'all',
          vantagePoint,
        },
      ),
    [graph, layout, t, vantagePoint],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<AssetNodeData>>(
    [],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(flowEdges);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [layoutEpoch, setLayoutEpoch] = useState(0);
  const focusedAssetIdRef = useRef<string | null>(focusedAssetId);
  const selectedNodeIdRef = useRef<string | null>(selectedNodeId);
  const isExpandingRef = useRef(isExpanding);
  const selectedNodeExpansionRef = useRef(selectedNodeExpansion);
  const lastCenteredAssetRef = useRef<string | null>(null);

  const layoutDependency = useMemo(
    () =>
      [
        flowNodes
          .map((node) => node.id)
          .sort()
          .join('\0'),
        flowEdges
          .map((edge) => `${edge.source}->${edge.target}`)
          .sort()
          .join('\0'),
        layout,
      ].join('|'),
    [flowNodes, flowEdges, layout],
  );

  const applySelectionState = useCallback(
    (node: Node<AssetNodeData>): Node<AssetNodeData> => {
      const isSelectedNode = selectedNodeIdRef.current === node.id;
      const selected = isSelectedNode || focusedAssetIdRef.current === node.id;
      const isNodeExpanding = isSelectedNode && isExpandingRef.current;
      const expansion = isSelectedNode
        ? selectedNodeExpansionRef.current
        : undefined;

      if (
        node.selected === selected &&
        Boolean(node.data.isActionTarget) === isSelectedNode &&
        Boolean(node.data.isExpanding) === isNodeExpanding &&
        node.data.expansion === expansion
      ) {
        return node;
      }

      return {
        ...node,
        selected,
        data: {
          ...node.data,
          isActionTarget: isSelectedNode,
          isExpanding: isNodeExpanding,
          expansion,
        },
      };
    },
    [],
  );

  useEffect(() => {
    setHoveredNodeId(null);
    setNodes((currentNodes) => {
      const currentNodeIds = new Set(currentNodes.map((node) => node.id));
      const preservePositions = shouldPreserveNodePositions({
        currentNodeIds,
        nextNodeIds: flowNodes.map((node) => node.id),
      });

      if (!preservePositions) {
        return flowNodes.map((node) => ({ ...node, hidden: true }));
      }

      return seedNodePositions({
        nodes: flowNodes,
        edges: flowEdges,
        previousPositions: new Map(
          currentNodes.map((node) => [node.id, node.position]),
        ),
      }).map(applySelectionState);
    });
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges, applySelectionState]);

  useEffect(() => {
    const previousFocusedAssetId = focusedAssetIdRef.current;
    const previousSelectedNodeId = selectedNodeIdRef.current;

    if (
      previousFocusedAssetId === focusedAssetId &&
      previousSelectedNodeId === selectedNodeId &&
      isExpandingRef.current === isExpanding &&
      selectedNodeExpansionRef.current === selectedNodeExpansion
    ) {
      return;
    }

    focusedAssetIdRef.current = focusedAssetId;
    selectedNodeIdRef.current = selectedNodeId;
    isExpandingRef.current = isExpanding;
    selectedNodeExpansionRef.current = selectedNodeExpansion;

    const affectedNodeIds = new Set([
      previousFocusedAssetId,
      focusedAssetId,
      previousSelectedNodeId,
      selectedNodeId,
    ]);

    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        affectedNodeIds.has(node.id) ? applySelectionState(node) : node,
      ),
    );
  }, [
    applySelectionState,
    focusedAssetId,
    isExpanding,
    selectedNodeExpansion,
    selectedNodeId,
    setNodes,
  ]);

  const layoutSpacing = MAIN_LAYOUT_SPACING_BY_GRAPH_LAYOUT[layout];

  const { isLayoutPending } = useAutoLayout<AssetNodeData>({
    layout: autoLayout,
    rankdir: RANK_DIRECTION_BY_GRAPH_LAYOUT[layout],
    nodesep: layoutSpacing.nodesep,
    ranksep: layoutSpacing.ranksep,
    fitViewPadding: layoutSpacing.fitViewPadding,
    fitViewMaxZoom: layoutSpacing.fitViewMaxZoom,
    layoutDependency,
    preserveViewportOnGrowth: true,
    waitForNodeMeasurement: false,
    sourceNodes: flowNodes,
    sourceEdges: flowEdges,
    onLayoutApplied: () => {
      setLayoutEpoch((epoch) => epoch + 1);
    },
  });

  useEffect(() => {
    if (layoutEpoch === 0) {
      return;
    }

    setNodes((currentNodes) => currentNodes.map(applySelectionState));
  }, [layoutEpoch, applySelectionState, setNodes]);

  useEffect(() => {
    if (!webAppClusters?.length || layoutEpoch === 0) {
      return;
    }

    const translateEdgeType = (edgeType: string) => {
      const translationKey = getGraphEdgeTranslationKey(edgeType);

      return translationKey ? t(translationKey) : edgeType;
    };

    let addedEdges: Edge[] = [];

    setNodes((currentNodes) => {
      const existingIds = new Set(currentNodes.map((node) => node.id));
      const nextNodes = [...currentNodes];
      const nextEdges: Edge[] = [];

      appendApplicationNodes({
        graph,
        clusters: webAppClusters,
        flowNodes: nextNodes,
        flowEdges: nextEdges,
        translateEdgeType,
        showEdgeLabels: graph.nodes.length <= 80,
      });

      const addedNodes = nextNodes.filter((node) => !existingIds.has(node.id));

      if (addedNodes.length === 0) {
        return currentNodes;
      }

      addedEdges = nextEdges;

      return [...currentNodes, ...addedNodes];
    });

    if (addedEdges.length > 0) {
      setEdges((currentEdges) => {
        const existingIds = new Set(currentEdges.map((edge) => edge.id));

        return [
          ...currentEdges,
          ...addedEdges.filter((edge) => !existingIds.has(edge.id)),
        ];
      });
    }
  }, [graph, layoutEpoch, setEdges, setNodes, t, webAppClusters]);

  useEffect(() => {
    if (!focusedAssetId) {
      lastCenteredAssetRef.current = null;

      return;
    }

    if (!nodesInitialized) return;
    const focusedNode = nodes.find((node) => node.id === focusedAssetId);

    if (!focusedNode) return;
    const { x, y } = focusedNode.position;
    const centerKey = `${focusedAssetId}:${x}:${y}`;

    if (lastCenteredAssetRef.current === centerKey) return;
    lastCenteredAssetRef.current = centerKey;
    const width = focusedNode.measured?.width ?? focusedNode.width ?? 180;
    const height = focusedNode.measured?.height ?? focusedNode.height ?? 110;

    void setCenter(x + width / 2, y + height / 2, { duration: 400, zoom: 1.3 });
  }, [focusedAssetId, nodes, nodesInitialized, setCenter]);

  const getNodeColor = useCallback((node: Node<AssetNodeData>) => {
    return GRAPH_NODE_COLOR[node.data.entityKind] ?? '#6b7280';
  }, []);

  const nodeActions = useMemo(
    () => ({
      onExpand: onExpandNode,
      onViewDetails,
      onCloseSelection: onClearSelection,
    }),
    [onExpandNode, onViewDetails, onClearSelection],
  );
  const handleNodeClick = useCallback(
    (_event: unknown, node: Node<AssetNodeData>) => {
      if (node.data.entityKind === 'APPLICATION' && node.data.applicationId) {
        onOpenApplication?.({
          applicationId: node.data.applicationId,
          isDraft: node.data.isDraft,
        });

        return;
      }

      if (isSyntheticGraphNodeId(node.id)) return;
      const parsed = ExternalNetworkEntityKindSchema.safeParse(
        node.data.entityKind,
      );

      if (!parsed.success) return;
      onSelectAsset(node.id, parsed.data);
    },
    [onOpenApplication, onSelectAsset],
  );
  const handleNodeDoubleClick = useCallback(
    (_event: unknown, node: Node<AssetNodeData>) => {
      if (isSyntheticGraphNodeId(node.id)) return;
      if (node.data.nodeType === 'cluster') return;
      onExpandNode(node.id);
    },
    [onExpandNode],
  );
  const handlePaneClick = useCallback(() => {
    onClearSelection();
  }, [onClearSelection]);
  const handleNodeMouseEnter = useCallback(
    (_event: unknown, node: Node<AssetNodeData>) => {
      setHoveredNodeId(isSyntheticGraphNodeId(node.id) ? null : node.id);
    },
    [],
  );
  const clearHoveredNode = useCallback(() => {
    setHoveredNodeId(null);
  }, []);
  const hoveredNode = hoveredNodeId
    ? nodes.find(
        (node) => node.id === hoveredNodeId && !node.data.isActionTarget,
      )
    : undefined;
  const hoveredTooltipPosition = hoveredNode
    ? flowToScreenPosition({
        x:
          hoveredNode.position.x +
          (hoveredNode.measured?.width ?? hoveredNode.width ?? 180) / 2,
        y: hoveredNode.position.y,
      })
    : null;

  return (
    <ExternalNetworkGraphNodeActionsProvider value={nodeActions}>
      <Box position="relative" w="full" h="full">
        <ReactFlow<Node<AssetNodeData>, Edge>
          colorMode={colorMode}
          nodes={nodes}
          edges={edges}
          style={{
            width: '100%',
            height: '100%',
            visibility: isLayoutPending ? 'hidden' : 'visible',
          }}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          onNodeDoubleClick={handleNodeDoubleClick}
          onPaneClick={handlePaneClick}
          onNodeMouseEnter={handleNodeMouseEnter}
          onNodeMouseLeave={clearHoveredNode}
          onNodeDragStart={clearHoveredNode}
          onMoveStart={clearHoveredNode}
          nodeTypes={externalNetworkNodeTypes}
          edgeTypes={externalNetworkEdgeTypes}
          minZoom={0.05}
          maxZoom={7}
          onlyRenderVisibleElements={onlyRenderVisibleElements}
          proOptions={{ hideAttribution: true }}
          attributionPosition="bottom-left"
          defaultEdgeOptions={{ type: 'externalNetworkEdge', animated: false }}
        >
          <Background
            variant={BackgroundVariant.Dots}
            color={GRAPH_COLORS.background}
            gap={12}
          />
          <Controls />
          {showMiniMap && (
            <MiniMap
              nodeStrokeWidth={0}
              nodeColor={getNodeColor}
              zoomable
              pannable
              position="bottom-right"
            />
          )}
        </ReactFlow>
        {isLayoutPending ? (
          <Box
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            pointerEvents="none"
          >
            <Spinner size="lg" />
          </Box>
        ) : null}
        {hoveredNode && hoveredTooltipPosition ? (
          <ExternalNetworkGraphNodeTooltip
            data={hoveredNode.data}
            position={hoveredTooltipPosition}
            translationNamespace={translationNamespace}
          />
        ) : null}
      </Box>
    </ExternalNetworkGraphNodeActionsProvider>
  );
}
